import type { FC } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  HolderOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';

const SUBTASKS_LIST_MAX_HEIGHT = 220;

export type TaskSubtaskFormValue = {
  name: string;
};

type SortableSubtaskRowProps = {
  id: string;
  name: number | string;
  onRemove: () => void;
};

const SortableSubtaskRow: FC<SortableSubtaskRowProps> = ({
  id,
  name,
  onRemove,
}) => {
  const { t } = useTranslation('maintenance');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        marginBottom: 8,
      }}
    >
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined />}
        {...attributes}
        {...listeners}
        aria-label={t('tasks.subtasks.dragHandle')}
        style={{ cursor: 'grab', marginTop: 4 }}
      />
      <Form.Item
        name={[name, 'name']}
        rules={[{ required: true, message: t('tasks.subtasks.nameRequired') }]}
        style={{ flex: 1, marginBottom: 0 }}
      >
        <Input placeholder={t('tasks.subtasks.namePlaceholder')} />
      </Form.Item>
      <Button
        type="text"
        danger
        size="small"
        icon={<MinusCircleOutlined />}
        onClick={onRemove}
        aria-label={t('tasks.subtasks.remove')}
        style={{ marginTop: 4 }}
      />
    </div>
  );
};

/** Optional ordered checklist lines; drag-and-drop reorder; scrolls when long. */
export const TaskSubtasksField: FC = () => {
  const { t } = useTranslation('maintenance');
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <Form.List name="subtasks">
      {(fields, { add, remove, move }) => {
        const ids = fields.map((field) => String(field.key));

        const onDragEnd = (event: DragEndEvent) => {
          const { active, over } = event;
          if (!over || active.id === over.id) return;
          const from = ids.indexOf(String(active.id));
          const to = ids.indexOf(String(over.id));
          if (from < 0 || to < 0) return;
          move(from, to);
        };

        return (
          <Form.Item
            label={t('tasks.subtasks.label')}
            extra={t('tasks.subtasks.hint')}
          >
            <div
              style={{
                maxHeight: SUBTASKS_LIST_MAX_HEIGHT,
                overflowY: fields.length > 4 ? 'auto' : undefined,
                paddingRight: 4,
              }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={ids}
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map((field) => (
                    <SortableSubtaskRow
                      key={field.key}
                      id={String(field.key)}
                      name={field.name}
                      onRemove={() => remove(field.name)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
            <Button
              type="dashed"
              onClick={() => add({ name: '' })}
              icon={<PlusOutlined />}
              block
              style={{ marginTop: 8 }}
            >
              {t('tasks.subtasks.add')}
            </Button>
          </Form.Item>
        );
      }}
    </Form.List>
  );
};

export function toSubtaskPayload(
  values: TaskSubtaskFormValue[] | undefined,
): Array<{ name: string; sequence: number }> {
  return (values ?? [])
    .map((item) => ({ name: (item.name ?? '').trim() }))
    .filter((item) => item.name.length > 0)
    .map((item, index) => ({ name: item.name, sequence: index + 1 }));
}
